/**
 * AmbientMesh — animated radial-gradient mesh used as the backdrop of
 * marketing + auth pages. Three blobs (violet, green, deep violet) drift
 * on long-period keyframes so the brand feels ALIVE without distracting
 * from the foreground content.
 *
 * Why CSS-only:
 *   - Zero asset weight (no PNG hero, no Lottie, no canvas).
 *   - GPU-composited transforms — buttery on any device that can paint a
 *     gradient.
 *   - `prefers-reduced-motion` is honored by the global `*` override in
 *     index.css, so accessibility is handled.
 *
 * Why two layers:
 *   - The body already paints a STATIC version of this mesh on every page
 *     so dashboard glass surfaces have something to refract. We don't
 *     animate that one (perf cost while customers do real work).
 *   - This component lays a SECOND, animated mesh over the body on the
 *     surfaces where motion is welcome — homepage + auth — for a richer
 *     first impression.
 *
 * Renders behind everything (`z-index: 0`, `pointer-events: none`).
 * Caller content needs `position: relative; z-index: 1` to sit on top.
 */
export function AmbientMesh() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes pb-blob-a {
          0%, 100% { transform: translate3d(-10%, -10%, 0) scale(1); }
          50%      { transform: translate3d(10%, 8%, 0) scale(1.1); }
        }
        @keyframes pb-blob-b {
          0%, 100% { transform: translate3d(8%, -4%, 0) scale(1.05); }
          50%      { transform: translate3d(-6%, 12%, 0) scale(1); }
        }
        @keyframes pb-blob-c {
          0%, 100% { transform: translate3d(0%, 4%, 0) scale(0.95); }
          50%      { transform: translate3d(-8%, -10%, 0) scale(1.1); }
        }
        .pb-mesh-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          will-change: transform;
        }
      `}</style>
      <div
        className="pb-mesh-blob"
        style={{
          top: '-15%',
          left: '-10%',
          width: '60vw',
          height: '60vw',
          background: '#7c4dff',
          animation: 'pb-blob-a 22s ease-in-out infinite',
        }}
      />
      <div
        className="pb-mesh-blob"
        style={{
          top: '20%',
          right: '-10%',
          width: '50vw',
          height: '50vw',
          background: '#7dffa2',
          opacity: 0.25,
          animation: 'pb-blob-b 18s ease-in-out infinite',
        }}
      />
      <div
        className="pb-mesh-blob"
        style={{
          bottom: '-20%',
          left: '30%',
          width: '55vw',
          height: '55vw',
          background: '#cdbdff',
          opacity: 0.3,
          animation: 'pb-blob-c 25s ease-in-out infinite',
        }}
      />
      {/*
        Vignette + subtle grain layer to keep the mesh from looking
        too "Bootstrap landing page." The radial vignette darkens the
        edges so content reads against a darker frame, and the noise
        is a 1×1 transparent SVG that JPEG-compresses out to nothing
        but breaks up the smoothness of pure CSS gradients.
      */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 30%, rgba(19,19,19,0.5) 100%)',
        }}
      />
    </div>
  );
}
